/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { isValidElement } from 'react';
import PropTypes from "prop-types";
import { useEuiTheme, cloneElementWithCss } from '../../services';
import { euiTextColorStyles } from './text_color.styles';
export var COLORS = ['default', 'subdued', 'success', 'accent', 'danger', 'warning', 'ghost', 'inherit'];
export var EuiTextColor = ({
  children,
  color = 'default',
  component = 'span',
  cloneElement = false,
  style,
  ...rest
}) => {
  const isNamedColor = COLORS.includes(color);
  const euiTheme = useEuiTheme();
  const styles = euiTextColorStyles(euiTheme);
  const cssStyles = [styles.euiTextColor, isNamedColor ? styles[color] : styles.customColor];

  // We're checking if is a custom color.
  // If it is a custom color we set the `color` of the `.euiTextColor` div to that custom color.
  // This way the children text elements can `inherit` that color and border and backgrounds can get that `currentColor`.
  const euiTextStyle = !isNamedColor ? {
    color: color,
    ...style
  } : {
    ...style
  };
  const props = {
    css: cssStyles,
    style: euiTextStyle,
    ...rest
  };
  if (isValidElement(children) && cloneElement) {
    const childrenStyle = {
      ...children.props.style,
      ...euiTextStyle
    };
    return cloneElementWithCss(children, {
      ...props,
      style: childrenStyle
    });
  } else {
    const Component = component;
    return <Component {...props}>{children}</Component>;
  }
};
EuiTextColor.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
       * Any of our named colors or a `hex`, `rgb` or `rgba` value.
       */
  color: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.any.isRequired]),
  /**
       * Determines the root element
       */
  component: PropTypes.oneOf(["div", "span"]),
  /**
       * Applies text styling to the child element instead of rendering a parent wrapper `span`/`div`.
       * Can only be used when wrapping a *single* child element/tag, and not raw text.
       */
  cloneElement: PropTypes.bool
};
try {
  EuiTextColor.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/text/text_color.tsx',
    description: '',
    displayName: 'EuiTextColor',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      color: {
        defaultValue: {
          value: 'default'
        },
        description: 'Any of our named colors or a `hex`, `rgb` or `rgba` value.',
        name: 'color',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text/text_color.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Color | "accent" | "success" | "warning" | "danger" | "default" | "subdued" | "ghost"'
        }
      },
      component: {
        defaultValue: {
          value: 'span'
        },
        description: 'Determines the root element',
        name: 'component',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text/text_color.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"span" | "div"',
          value: [{
            value: '"span"'
          }, {
            value: '"div"'
          }]
        }
      },
      cloneElement: {
        defaultValue: {
          value: 'false'
        },
        description: 'Applies text styling to the child element instead of rendering a parent wrapper `span`/`div`.\n' + 'Can only be used when wrapping a *single* child element/tag, and not raw text.',
        name: 'cloneElement',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text/text_color.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}